import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import {
  Email as EmailIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { authApi } from '@api/auth'
import { apiErrorMessage } from '@api/index'
import { ForgotPasswordData } from '@types'

const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const { control, handleSubmit, formState: { errors } } = useForm<ForgotPasswordData>({
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = async (data: ForgotPasswordData) => {
    setIsLoading(true)
    setError('')
    setSuccessMessage('')

    try {
      const response = await authApi.forgotPassword(data)
      // The server deliberately answers the same way whether or not the address
      // is registered, so show its wording rather than implying an account exists.
      setSuccessMessage(
        response.data?.data?.message ||
        'If an account exists for that email address, a password reset link is on its way.'
      )
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to send reset instructions'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography
        variant="h4"
        component="h1"
        align="center"
        gutterBottom
        sx={{ fontWeight: 'bold', color: 'primary.main' }}
      >
        Forgot Password
      </Typography>

      <Typography
        variant="body1"
        align="center"
        color="textSecondary"
        sx={{ mb: 3 }}
      >
        Enter your email address and we'll send you instructions to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Controller
        name="email"
        control={control}
        rules={{
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address',
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Email"
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            margin="normal"
            autoComplete="email"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading || !!successMessage}
        sx={{ mt: 3, mb: 2, py: 1.5 }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Sending...
          </>
        ) : (
          'Send Reset Instructions'
        )}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          to="/auth/sign-in"
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <BackIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          <Typography variant="body2" color="primary">
            Back to Sign In
          </Typography>
        </Link>
      </Box>
    </Box>
  )
}

export default ForgotPassword
